/* ==========================================================================
   Custom Y2K Avenue — JS global
   Chargé via snippets/custom-globals.liquid. Script classique (pas de module),
   volontairement indépendant de l'importmap d'Horizon pour survivre aux updates.
   ========================================================================== */

/* --------------------------------------------------------------------------
   Vue mannequin — pages collection
   Bascule toutes les cartes produit sur leur 2e image. La 2e image est déjà
   présente dans le DOM grâce au réglage natif « show_second_image_on_hover »
   (voir snippets/card-gallery.liquid), donc aucune requête supplémentaire.
   Le choix est mémorisé dans localStorage. En vue mannequin, l'effet de survol
   est neutralisé : l'image reste figée sur la photo portée.
   -------------------------------------------------------------------------- */
(() => {
  'use strict';

  const STORAGE_KEY = 'y2k:model-view';
  const BUTTON_CLASS = 'custom-model-view';

  const readPref = () => {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch (error) {
      return false;
    }
  };

  const writePref = (on) => {
    try {
      localStorage.setItem(STORAGE_KEY, on ? '1' : '0');
    } catch (error) {
      /* navigation privée ou stockage bloqué : le toggle marche quand même, sans mémoire */
    }
  };

  let modelView = readPref();

  /**
   * Bascule une carte produit sur sa 2e image, ou la remet sur la première.
   * Les produits à une seule image sont ignorés : ils gardent leur photo.
   */
  const applyToCard = (card) => {
    const gallery = card.querySelector('[ref="cardGallery"]');
    const slideshow = card.querySelector('slideshow-component');

    if (!gallery || !slideshow || typeof slideshow.select !== 'function') return;

    const slides = slideshow.slides;
    if (!slides || slides.length < 2) return;

    if (modelView) {
      // Fige l'image : on retire les hooks de survol d'Horizon en mémorisant leur valeur.
      if (gallery.hasAttribute('on:pointerenter')) {
        gallery.dataset.customHoverEnter = gallery.getAttribute('on:pointerenter');
        gallery.dataset.customHoverLeave = gallery.getAttribute('on:pointerleave') || '';
        gallery.removeAttribute('on:pointerenter');
        gallery.removeAttribute('on:pointerleave');
      }

      if (slideshow.current !== 1) slideshow.select(1, undefined, { animate: false });
    } else {
      if ('customHoverEnter' in gallery.dataset) {
        gallery.setAttribute('on:pointerenter', gallery.dataset.customHoverEnter);
        if (gallery.dataset.customHoverLeave) {
          gallery.setAttribute('on:pointerleave', gallery.dataset.customHoverLeave);
        }
        delete gallery.dataset.customHoverEnter;
        delete gallery.dataset.customHoverLeave;
      }

      if (slideshow.current !== 0) slideshow.select(0, undefined, { animate: false });
    }
  };

  const applyAll = () => {
    document.querySelectorAll('product-card').forEach(applyToCard);
  };

  const syncButton = (button) => {
    button.setAttribute('aria-checked', modelView ? 'true' : 'false');
  };

  /** Branche le clic une seule fois, que le bouton vienne du bloc Liquid ou du repli JS. */
  const wireButton = (button) => {
    if (button.dataset.customWired === '1') return;
    button.dataset.customWired = '1';

    button.addEventListener('click', () => {
      modelView = !modelView;
      writePref(modelView);
      document.querySelectorAll('.' + BUTTON_CLASS).forEach(syncButton);
      applyAll();
    });
  };

  /** Repli : markup identique au bloc blocks/custom-model-view-toggle.liquid. */
  const buildToggle = () => {
    const button = document.createElement('button');

    button.type = 'button';
    button.className = BUTTON_CLASS;
    button.setAttribute('role', 'switch');
    button.innerHTML =
      '<span class="custom-model-view__track"><span class="custom-model-view__thumb"></span></span>' +
      '<span class="custom-model-view__label">Vue mannequin</span>';

    return button;
  };

  /*
    Le toggle n'existe que sur les pages collection, dans la barre filtres / tri.
    Si Victor a posé le bloc « Toggle vue mannequin » sur la page, on déplace CE
    bouton (il porte ses réglages de design). Sinon on en injecte un par défaut.
  */
  const injectToggle = () => {
    const collection = document.querySelector('collection-component');
    if (!collection) return;

    const wrapper = collection.querySelector('.facets-controls-wrapper');
    if (!wrapper) return;

    let button = wrapper.querySelector('.' + BUTTON_CLASS);

    if (!button) {
      // Un bouton rendu par le bloc Liquid, encore à sa place d'origine dans la page.
      button = document.querySelector('.' + BUTTON_CLASS);
      wrapper.appendChild(button || buildToggle());
      button = wrapper.querySelector('.' + BUTTON_CLASS);
    }

    if (!button) return;

    syncButton(button);
    wireButton(button);
  };

  /*
    La grille est re-rendue par la Section Rendering API (filtres, tri, scroll infini).
    On réapplique après chaque morph. Les gardes `slideshow.current !== x` et
    `querySelector('.' + BUTTON_CLASS)` rendent l'opération idempotente, donc
    l'observer converge au lieu de boucler sur ses propres mutations.
  */
  let scheduled = false;
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      injectToggle();
      applyAll();
    });
  };

  const watchResults = () => {
    const list = document.querySelector('results-list');
    if (!list) return;

    new MutationObserver(schedule).observe(list, { childList: true, subtree: true });
  };

  const init = async () => {
    if (!document.querySelector('collection-component')) return;

    // Attend que les composants Horizon soient définis, sinon slideshow.select n'existe pas encore.
    await Promise.all([
      customElements.whenDefined('slideshow-component'),
      customElements.whenDefined('product-card'),
    ]);

    injectToggle();
    applyAll();
    watchResults();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* --------------------------------------------------------------------------
   Recommandations produit (custom) — blocks/custom-product-recommendations.liquid
   Met à jour le prix affiché quand on change de variante dans une carte.
   L'ajout au panier n'est pas géré ici : le <select> porte name="id" et le
   <product-form-component> natif d'Horizon fait le reste.
   Écoute déléguée sur document : les cartes arrivent après coup (chargement
   asynchrone des recommandations) et sont re-rendues par morph.
   -------------------------------------------------------------------------- */
(() => {
  'use strict';

  document.addEventListener('change', (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-custom-reco-select]')) return;

    const card = select.closest('.custom-reco__card');
    const option = select.selectedOptions[0];
    if (!card || !option) return;

    const price = card.querySelector('[data-custom-reco-price]');
    const compare = card.querySelector('[data-custom-reco-compare]');

    if (price) price.innerHTML = option.dataset.price || '';
    if (compare) {
      compare.innerHTML = option.dataset.compare || '';
      compare.hidden = !option.dataset.compare;
    }
  });
})();
